import { useContext, useState } from 'react';
import { SelfCompanyContext, editCompanyModalId } from 'src/pages/myCompany/MyCompany';
import Button from 'src/components/button/Button';
import {
  ActionIcon,
  Card,
  ConfirmModal,
  Dropdown,
  SubMenuModal,
  TDropdown,
  TDropdownOptions,
  Tooltip,
  Typography
} from 'src/components/common';
import { ChatIcon, MoreOptionsIcon } from 'src/components/common/common.icons';
import { assignSubMenuModalId } from 'src/store/common/common.slice';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { StorageUtils, textEllipsis, textFormatter } from 'src/utils';
import ManageProfilePicture from 'src/components/common/profile/manageProfilePicture/ManageProfilePicture';
import {
  contactUsModalStateChange,
  deleteCompanyProfilePicture,
  getCompanyPresignedUrl
} from 'src/store/company/company.slice';
import {
  TDeleteCompanyProfilePictureRequest,
  TFindCreateConversationRequest,
  TFollowRequest,
  TGetCompany,
  TGetCompanyPresignedUrlActionRequest
} from 'src/services/apiEndpoint.types.ts';
import { useGetIdentity, useOpenInNewTab, useUrlParamValue } from 'src/hooks';
// import { TUserTypes } from 'src/constants/user.constants';
import { follow, unFollow } from 'src/store/socialMedia/socialMedia.slice';

import './companyProfileCard.scss';
import EditCompany from '../editCompany/EditCompany';
import { useNavigate } from 'react-router-dom';
import { findCreateConversation } from 'src/store/chat/chat.slice';
import ContactUsModal from '../contactUsModal/ContactUsModal';
import Company from 'src/assets/images/svg/Company.svg';
import IncubatorBadge from 'src/assets/images/svg/IncubatorBadge.svg';
import LikeUserModal from 'src/components/common/LikeUserModal/LikeUserModal';
import { TLikeUser } from 'src/components/posts/postCard/PostCard';

export type TCompanyProfileCard = {
  viewCompanyId?: string;
  name: string;
  type: string;
  introduction?: string;
  website?: string;
  contactUsUrl?: string;
  profilePic?: string;
  dropdownItems: TDropdownOptions[];
  getUserStats?: ({ id }: { id: string }) => void;
};

const CompanyProfileCard = ({
  viewCompanyId,
  name = '-',
  type = '-',
  introduction = '-',
  website = ' ',
  contactUsUrl = ' ',
  profilePic = '-',
  dropdownItems,
  getUserStats
}: TCompanyProfileCard) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dropdownId, modalId } = useAppSelector((store) => store.common);
  const { getIdentities } = useGetIdentity();
  const { paramValue } = useUrlParamValue();
  const { profile } = useAppSelector((store) => store.profile);
  const {
    myCompany,
    viewCompany,
    contactUsModalState
  }: {
    myCompany: TGetCompany;
    contactUsModalState?: { state: boolean };
    viewCompany?: TGetCompany;
  } = useAppSelector((store) => store.company);
  const isAuthorized: boolean = useAppSelector((store) => store.auth.isAuthorized);
  const userStats = useAppSelector(
    (store) => store.socialMedia.userStats
  );
  const { followerCount, followingCount, postCount, isFollowing, announcementCount } = userStats

  const { openInNewTab } = useOpenInNewTab();

  // state
  const [userModalOpen, setUserModal] = useState<boolean>(false);
  const [likedUser, setLikedUser] = useState<TLikeUser[]>([]);
  const [key, setKey] = useState('')

  // Constants
  const { authenticatedId, loggedInUserType, loggedInCompanyId } = getIdentities();

  const isSelfCompany = useContext(SelfCompanyContext); // Whether this profile card is viewed by self.

  const companyId = StorageUtils.get('companyId') as string;
  const isOwnCompany = viewCompanyId ? loggedInCompanyId === viewCompanyId : isSelfCompany;
  // const userType = StorageUtils.get('userType') as TUserTypes;

  // Utils
  // const companyNameWitEllipsis = textEllipsis({ text: name, charLimit: 20 });
  const companyNameWitEllipsis = textEllipsis({ text: name, charLimit: 20 });

  const [show, setShow] = useState<boolean>(false)

  const dropdownOptions: TDropdown = {
    id: 'company-profile-card-dropdown',
    size: 'large',
    alignment: 'center',
    items: dropdownItems
  };

  const openProfileCardDropdown = () => {
    if (dropdownId) {
      dispatch(assignSubMenuModalId(''));
    } else {
      dispatch(assignSubMenuModalId(dropdownOptions.id));
    }
  };
  function handleUserModalOpen(key: string) {
    setKey(key)
    if (key === 'follower' && userStats?.followerUsers?.length > 0) {
      setLikedUser(userStats?.followerUsers)
      setUserModal(true)
    } else if (key === 'following' && userStats?.followingUsers?.length > 0) {
      setLikedUser(userStats?.followingUsers)
      setUserModal(true)
    }
  }

  const profileStats = [
    { key: 'follower', label: 'Follower', value: followerCount, onclick: handleUserModalOpen },
    { key: 'following', label: 'Following', value: followingCount, onclick: handleUserModalOpen },
    { key: 'post', label: 'Post', value: postCount },
    { key: 'announcements', label: 'Announcements', value: announcementCount || 0 }
  ];

  const handleRedirectionToUrl = ({ url }: { url: string }) => {
    if (url) {
      openInNewTab({ url });
    }
  };

  const handleUploadPicture = async ({ data }: { data: string | ArrayBuffer | null }) => {
    const payload: TGetCompanyPresignedUrlActionRequest = {
      id: companyId,
      data,
      showPopup: true
    };
    dispatch(getCompanyPresignedUrl(payload));
  };

  const handleDeletePicture = () => {
    const payload: TDeleteCompanyProfilePictureRequest = { id: companyId };

    dispatch(deleteCompanyProfilePicture(payload));
  };
  const [loading, setLoading] = useState<boolean>(false);
  const handleFollow = () => {
    if (!isAuthorized) {
      setShow(true)
      return
    }
    const payload: TFollowRequest = {
      userId: authenticatedId,
      userType: loggedInUserType,
      followUserId: viewCompanyId || '0',
      followUserType: 'Company'
    };
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1800);

    if (isFollowing) {
      dispatch(unFollow(payload)).then(() => {
        if (getUserStats && viewCompanyId) {
          getUserStats({ id: viewCompanyId });
        }
      });
    } else {
      dispatch(follow(payload)).then(() => {
        if (getUserStats && viewCompanyId) {
          getUserStats({ id: viewCompanyId });
        }
      });
    }
  };
  function handleNavigate() {
    navigate('/login?prev=' + encodeURIComponent(`/view-company?id=${viewCompanyId}`));
  }

  return (
    <>
      {userModalOpen && <LikeUserModal title={key} users={likedUser} isModalOpen={userModalOpen} handleCloseModal={() => setUserModal(false)} />}
      {contactUsModalState.state && <ContactUsModal viewCompanyId={viewCompanyId} />}
      <Card className="company-profile-card">
        <div className="company-profile-card__row">
          <div className="company-profile-card__row__picture">
            <ManageProfilePicture
              profilePic={profilePic || ''}
              name={name}
              isSelfUser={isSelfCompany}
              uploadFunc={handleUploadPicture}
              deleteFunc={handleDeletePicture}
            />
          </div>
          <div className="company-profile-card__row__content">
            <div className="company-profile-card__row__content__header">
              <div className="company-profile-card__row__content__header__company-info">
                <Tooltip
                  text={name}
                  conditionToDisplay={companyNameWitEllipsis?.hasEllipsis}
                  limitWidth
                >
                  <Typography as="h5" variant="subHeading-2" fontWeight="semiBold">
                    {/* {textFormatter({ value: companyNameWitEllipsis?.text })} */}
                    {textFormatter({ value: name })}
                    {(type === "Incubator" || type === "Accelerator" || type === 'Pre-Incubator' || type === 'E-Cell') && (<img style={{ marginLeft: "8px", height: "20px", width: "20px", marginBottom: "4px" }} src={IncubatorBadge} alt="Incubator" />)}
                    {(type === "Start up" || type === "Service Provider") && (<img style={{ marginLeft: "8px", height: "20px", width: "20px", marginBottom: "4px" }} src={Company} alt="Company" />)}
                  </Typography>
                </Tooltip>
                <Typography as="p" variant="body-2" fontWeight="regular">
                  {textFormatter({ value: type })}
                </Typography>
              </div>
              <div className="company-profile-card__row__content__header__actions">
                <>
                  {/* {!isOwnCompany && <ActionIcon
                    className="profile-card__content__header__actions__chat"
                    onClick={chatToUser}
                  >
                    <ChatIcon />
                  </ActionIcon>} */}
                  <div
                    className="post-card__header__actions__dropdown"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfileCardDropdown();
                    }}
                  >
                    {<MoreOptionsIcon />}{' '}
                    <SubMenuModal>
                      <Dropdown {...dropdownOptions} />
                    </SubMenuModal>
                  </div>
                </>
              </div>
            </div>
            <Typography
              as="p"
              variant="caption"
              fontWeight="regular"
              className="company-profile-card__row__content__introduction"
            >
              {introduction}
            </Typography>
            <div className="company-profile-card__row__content__stats">
              {profileStats.map(({ key, label, value, onclick }) => {
                return (
                  <div key={key} className="company-profile-card__row__content__stats__stat"
                  >
                    <div className="company-profile-card__row__content__stats__stat__values"
                    onClick={() => onclick && isSelfCompany && onclick(key)}
                    style={onclick && isSelfCompany ? { cursor: 'pointer' } : {}}
                    >
                      <Typography
                        as="p"
                        variant="caption"
                        fontWeight="regular"
                        className="company-profile-card__row__content__stats__stat__values__label"
                      >
                        {label}
                      </Typography>
                      <Typography
                        as="p"
                        variant="body-1"
                        fontWeight="semiBold"
                        className="company-profile-card__row__content__stats__stat__values__value"
                      >
                        {value || 0}
                      </Typography>
                    </div>
                    <hr></hr>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="company-profile-card__footer">
          {!isSelfCompany && (
            <Button
              variant={isFollowing ? 'secondary' : 'primary'}
              size="small"
              disabled={loading}
              className="company-profile-card__footer__button"
              onClick={handleFollow}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
          )}
          {website && website.trim() && (
            <Button
              variant={isSelfCompany ? 'primary' : 'secondary'}
              size="small"
              className="company-profile-card__footer__button"
              onClick={() => {
                handleRedirectionToUrl({ url: website.trim() });
              }}
            >
              Website
            </Button>
          )}
          {!isOwnCompany &&
            (myCompany?.type === 'Incubator' ||
              myCompany?.type === 'Pre-Incubator' ||
              myCompany?.type === 'Investor' ||
              myCompany?.type === 'Accelerator' ||
              myCompany?.type === 'Mentor' ||
              profile?.role === 'Mentor' ||
              profile?.role === 'Investor' ||
              profile?.role === 'Incubator' ||
              profile?.role === 'Pre-Incubator' ||
              (myCompany?.type !== 'Incubator' &&
                profile?.role !== 'Investor' &&
                profile?.role !== 'Incubator' &&
                (profile?.role === 'Entrepreneur' || profile?.role === 'Aspiring Entrepreneur') &&
                viewCompany?.type !== 'Incubator' &&
                viewCompany?.type !== 'Pre-Incubator' &&
                viewCompany?.type !== 'Accelerator' &&
                viewCompany?.type !== 'Incubator' &&
                viewCompany?.type !== 'Investor')) && (
              <Button
                variant={'secondary'}
                size="small"
                className="company-profile-card__footer__button"
                onClick={() => {
                  if (!isAuthorized) {
                    navigate(`/login?prev=/view-company?id=${viewCompanyId}`)
                    return
                  }
                  dispatch(contactUsModalStateChange({ state: true }))
                }}
              >
                Contact Us
              </Button>
            )}
          {/* {contactUsUrl && contactUsUrl.trim() && (
            <Button
              variant={isSelfCompany ? 'secondary' : 'tertiary'}
              size="small"
              className="company-profile-card__footer__button"
              onClick={() => {
                handleRedirectionToUrl({ url: contactUsUrl.trim() });
              }}
            >
              Contact us
            </Button>
          )} */}
        </div>
      </Card>
      {isSelfCompany && modalId === editCompanyModalId && (
        <EditCompany isModalOpen={modalId === editCompanyModalId} />
      )}
      <ConfirmModal
        title=""
        description={<p style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', margin: '0' }}>
          <span>To enjoy all the features of <span style={{ color: '#fa4616' }}>htraction</span> </span>
          <span><span style={{ color: '#fa4616' }}>Sign in</span> to Continue</span>
        </p>}
        openConfirmModal={show}
        onClose={() => setShow(false)}
        onSubmit={handleNavigate}
        submitText="Sign in"
        type="confirm"
      />
    </>
  );
};

export default CompanyProfileCard;
